import * as React from "react";
import classnames from "classnames";
import { Link } from "react-router-dom";
import { findIndex } from "lodash";
// # models
import { PlanModel } from "@/models";
// # services
import { plansService } from "@/services/api";
import { route } from "@/services";
// # hooks
import { useIsBusy } from "@/hooks";
// # components
import { PlanSceneRouter } from "./";
import { Tag } from "@/components/content";
import { Spinner } from "@/components/feedback";
import { Table } from "@/components/table";

interface iProps {}

const tableColumns = [
  {
    key: "name",
    label: "Name",
    render: (plan: PlanModel) => {
      const isDisabled = !plan.isActive();

      return (
        <td
          className={classnames("plan-list-item", "table__cell", {
            "table__cell--disabled": isDisabled,
          })}
        >
          <h4 className="plan-list-item__name">{plan.data.name}</h4>
          {isDisabled ? <Tag theme="disabled">Deactivated</Tag> : null}
        </td>
      );
    },
  },
  {
    key: "edit",
    label: "",
    render: (plan: PlanModel) => (
      <td>
        <Link
          className="btn-c2a style-secondary"
          to={route("admin.plans.edit").make(plan.data.id)}
        >
          Edit
        </Link>
      </td>
    ),
  },
];

/**
 * Renders a list of subscription plans.
 *
 * @type {React.SFC}
 */
const PlanList: React.SFC<iProps> = (props: iProps) => {
  // Hooks: load plans from the API.
  const plansResolver = React.useMemo(() => plansService.get({}), []);
  const [plans, setPlans] = React.useState<PlanModel[]>([]);
  React.useEffect(() => {
    plansResolver.then((response) => {
      setPlans(response.data);
    });
  }, [plansResolver]);
  // Hooks: manage busy state.
  const isBusy = useIsBusy([plansResolver]);

  /**
   * Manages state changes when a plan is added or updated.
   *
   * @param {PlanModel} updatedPlan
   * @returns {void}
   */
  const handlePlanChange = (updatedPlan: PlanModel): void => {
    if (!updatedPlan.data.id) {
      return;
    }
    const index = findIndex(
      plans,
      (plan) => plan.data.id === updatedPlan.data.id,
    );

    // add to the top of the list if it doesn't exist
    if (index === -1) {
      setPlans([updatedPlan, ...plans]);
    } else {
      plans[index] = updatedPlan;
      setPlans(plans);
    }
  };

  return (
    <>
      {isBusy ? <Spinner /> : null}
      <Table className="plan-list" columns={tableColumns} rows={plans} />
      <PlanSceneRouter onPlanChange={handlePlanChange} />
    </>
  );
};

export default PlanList;
